import type { InferOutput } from 'valibot'
import { array, nullish, number, object, omit, optional, picklist, pipe, string, transform } from 'valibot'
import { unwrapMarginText } from '~/utils/html'
import { lastUpdatedByEditor } from '#shared/utils/solr-fields'

export const baseCommentarySchema = object({
  id: nullish(string()),
  bodytext: nullish(string()),
  lawFragmentId: nullish(string()),
  lawId: nullish(string()),
  shortTitle: nullish(string()),
  authorName: nullish(array(string())),
  authorId: nullish(array(number())),
  authorSlug: nullish(array(string())),
  author_list: nullish(array(string())),
  lastUpdatedByEditor,
  chapterTitle: nullish(string()),
  paragraphTitle: nullish(string()),
  path_location: nullish(array(string())),
  ezMainNodeId: nullish(number()),
  infoNoticeObjectId: nullish(number()),
  legal_areas: nullish(array(string())),
  curatedReferences: nullish(pipe(
    array(string()),
    transform((val: string[]) => {
      const filtered = val.filter(ref => ref?.trim())
      return filtered.length > 0 ? filtered : []
    }),
  )),
  nodeType: picklist([
    'legal_commentary',
    'legal_commentary_section',
  ]),
  originalPublicationDate: nullish(string()),
  editionId: optional(string()),
})

export const commentarySchema = pipe(
  omit(baseCommentarySchema, ['nodeType']),
  transform(({ authorName, lastUpdatedByEditor, bodytext, id, ...rest }) => {
    // NS standard texts (e.g. NS 8407) carry meaningful `<br>` tags in their
    // bodytext that authors expect to render as line breaks. Detect those via
    // the `ns-` id prefix; commentary docs use a different id format and keep
    // the default stripping behavior.
    const isNsLawNode = id?.toLowerCase().startsWith('ns-') ?? false

    // Normalize Solr rich text (line breaks and class names)
    let normalizedText = normalizeSolrRichText(bodytext ?? '', { preserveBr: isNsLawNode })

    if (isNsLawNode) {
      normalizedText = convertNsListMarkers(normalizedText)
      normalizedText = stripBrInOrderedLists(normalizedText)
      normalizedText = convertNsBrToBreaks(normalizedText)
    }

    // Temp workaround for Tvisteloven commentary
    // NOTE: Does not actually apply in 99% of cases
    normalizedText = unwrapMarginText(normalizedText)

    return {
      ...rest,
      id,
      bodytext: normalizedText,
      authors: [...(authorName ?? [])],
      lastUpdated: lastUpdatedByEditor,
    }
  }),
)

export type BaseCommentary = InferOutput<typeof commentarySchema>
